import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Make from '../MakeSection/Make';
import FocusMarketing from '../FocusMarketingSection/FocusMarketing';
import IssueSection from '../IssuesSection/Issue';
import LatestArticles from '../LatestArticles/LatestArticles';
import HeroSection from '../HeroSection/HeroSection';
import SEO from '../SEO/SEO';
import { useTranslation } from 'react-i18next';
import { useCachedFetch } from '../../hooks/useCachedFetch';
import ErrorPage from '../../pages/404Page';

const MarketingItem = ({ hideHeaderFooterSetter }) => {
  const { pageSlug } = useParams();
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const [isLoading, setIsLoading] = useState(true);
  const [mainContent, setMainContent] = useState(null);
  const [seoData, setSeoData] = useState(null);

  const pageKeyMapping = {
    'crafted-creativity': 'crafted-creativity',
    'growth-strategy': 'growth-strategy',
    'digital-advertising': 'digital-advertising',
    'content-promotion': 'content-promotion',
    'sales-automation': 'sales-automation',
    'data-insights': 'data-insights'
  };

  const pageKey = pageKeyMapping[pageSlug];

  const { data: mainJsonData, loading: dataLoading, error: dataError } = useCachedFetch('/data/locales/mainData.json');

  useEffect(() => {
    if (dataLoading) return;
    
    if (dataError) {
      console.error('Error loading marketing item data:', dataError);
      setMainContent(null);
      setSeoData(null);
      setIsLoading(false);
      return;
    }

    if (!mainJsonData?.TopMainData || !mainJsonData.TopMainData[pageSlug]) {
      console.error(`Page slug "${pageSlug}" not found in TopMainData`);
      setMainContent(null);
      setSeoData(null);
      setIsLoading(false);
      return;
    }

    const content = mainJsonData.TopMainData[pageSlug];

    if (!content) {
      console.error(`No content found for page "${pageSlug}"`);
      setMainContent(null);
      setSeoData(null);
    } else {
      setMainContent(content);
      
      if (content.seo) {
        const title = content.seo.title?.[currentLang] || content.seo.title?.en || pageSlug;
        const description = content.seo.description?.[currentLang] || content.seo.description?.en || '';
        const keywords = content.seo.keywords?.[currentLang] || content.seo.keywords?.en || '';
        const type = content.seo.type || 'website';
        const robots = content.seo.robots || 'index, follow';
      
        const robotsParts = robots.split(', ');
        const noIndex = robotsParts.includes('noindex');
        const noFollow = robotsParts.includes('nofollow');
        
        setSeoData({ title, description, keywords, type, noIndex, noFollow });
      } else {
        const title = content.mainTitle?.[currentLang] || content.mainTitle?.en || pageSlug;
        const description = content.mainSubtitle?.[currentLang] || content.mainSubtitle?.en || '';
        
        setSeoData({ title, description, keywords: '', type: 'website', noIndex: false, noFollow: false });
      }
    }
    
    setIsLoading(false);
  }, [mainJsonData, dataLoading, dataError, pageSlug, currentLang]);

  if (isLoading) {
    return null;
  }

  if (!mainContent || !seoData) {
    return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} />;
  }

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        type={seoData.type}
        noIndex={seoData.noIndex}
        noFollow={seoData.noFollow}
      />
      <HeroSection page={pageSlug} showRecentWork={false} showMoreButton={true} />
      <IssueSection page={pageSlug} />
      <FocusMarketing page={pageSlug} />
      <LatestArticles/>
      <Make pageKey={pageKey} />
    </>
  );
};

export default MarketingItem;